import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { toast } from 'react-toastify';
import styles from './MemberFormModal.module.css';
import { participationStatusOptions } from 'config/admin/clubMembers';
import {
  createClubMember,
  updateMember,
  lookupUserByEmail,
} from 'api/userApi';
import {
  getUserEmail,
  getUserPermissionLevel,
} from 'services/auth/tokenService';

import NameInputBox from 'components/common/NameInputBox';
import CustomInput from 'components/common/CustomInput/CustomInput';
import CustomSelect from 'components/common/CustomSelect';
import {
  notifyAthletePromotions,
  notifyContactPromotions,
} from 'utils/promotionToast';
import { UserPermissionLevel } from 'constants/auth';
import CustomDropdown from 'components/NewDesign/CustomDropdown/CustomDropdown';
import UserLookupConfirmDialog from 'components/admin/UserLookupConfirmDialog';

export const ROLE_OPTIONS = [
  {
    value: 'Admin',
    label: 'Admin',
    text: 'An administrator can manage members and create and edit meets and results',
  },
  {
    value: 'Member',
    label: 'Member',
    text: 'A member can view results and meets, but cannot edit content',
  },
];

// Guest is no longer offered as a role: the guests feature is hidden for every club (Doug, 2026-10-11). It is added
// to the options ONLY when editing someone who is already a Guest, so the field shows their real role (instead of
// rendering blank) and the admin can keep it or change them to Member/Admin.
const GUEST_ROLE_OPTION = {
  value: 'Guest',
  label: 'Guest',
  text: 'A Guest is a temporary member who can view all meets and results but cannot edit content',
};

// Visitor isn't an admin-assignable role — it's minted with a visiting athlete (and hard-removed with it). It's appended
// to the options ONLY when editing a contact who is already a Visitor, so the field shows their real role (instead of
// rendering blank) and the admin can keep it or promote to Member/Admin to adopt the family. Guest is deliberately
// omitted for a visitor contact: converting straight to Guest would leave the family's athletes as visitors and orphan
// the contact at visitor-expiry. To make a visiting family into guests, change their athletes (the contact follows).
const VISITOR_ROLE_OPTION = {
  value: 'Visitor',
  label: 'Visitor',
  text: 'A meet-sourced contact for a visiting athlete. Promote to Member to adopt the family into your club.',
};

// The roles offered for a person whose role in the club is currently `currentRole` (undefined for someone new):
// Admin and Member, plus their own role when it is one that is not otherwise offered. An existing Visitor shows
// Visitor (so the field isn't blank) beside the promotion targets Member/Admin; an existing Guest shows Guest.
// Guest is never offered to a visitor contact (see VISITOR_ROLE_OPTION + the server guard).
export const roleOptionsFor = (currentRole) =>
  currentRole === 'Visitor'
    ? [...ROLE_OPTIONS, VISITOR_ROLE_OPTION]
    : currentRole === 'Guest'
      ? [...ROLE_OPTIONS, GUEST_ROLE_OPTION]
      : ROLE_OPTIONS;

const MemberFormModal = ({
  show,
  member = null,
  clubId,
  onClose,
  onSuccess,
}) => {
  const isEdit = !!member;
  const userPermissionLevel = getUserPermissionLevel();
  const currentUserEmail = getUserEmail();

  const isEditingSelf = isEdit && member && currentUserEmail === member.email;
  const isPrivilegedUser = userPermissionLevel >= UserPermissionLevel.Official;

  // Email is admin-managed: only Admin/Owner may change a member's registered email — it's the cross-system source
  // of truth. See docs/secondary-contact-family-ui-plan.html §16.
  const canEditEmail = userPermissionLevel >= UserPermissionLevel.Admin;
  const canEditParticipationStatus = !isEditingSelf || isPrivilegedUser;
  const canEditRole = !isEditingSelf || isPrivilegedUser;

  const [formValues, setFormValues] = useState({
    firstName: '',
    lastName: '',
    email: '',
    participationStatus: 'Active',
    role: 'Member',
  });

  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);

  // Preflight lookup state — only used on the Add flow.
  const [lookupResult, setLookupResult] = useState(null);
  const [showLookupDialog, setShowLookupDialog] = useState(false);

  useEffect(() => {
    if (isEdit && member) {
      setFormValues({
        firstName: member.firstName || '',
        lastName: member.lastName || '',
        email: member.email || '',
        participationStatus: member.participationStatus || 'Active',
        role: member.role || 'Member',
      });
    } else {
      setFormValues({
        firstName: '',
        lastName: '',
        email: '',
        participationStatus: 'Active',
        role: 'Member',
      });
    }
    setErrors({});
    setLookupResult(null);
    setShowLookupDialog(false);
  }, [isEdit, member, show]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormValues((prev) => ({ ...prev, [name]: value }));
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: '' }));
    }
  };

  const handleDropBoxChange = (name, value) => {
    setFormValues((prev) => ({ ...prev, [name]: value }));
  };

  const validateForm = () => {
    const newErrors = {};
    if (!formValues.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/\S+@\S+\.\S+/.test(formValues.email)) {
      newErrors.email = 'Email is invalid';
    }
    if (!formValues.participationStatus) {
      newErrors.participationStatus = 'Membership status is required';
    }
    if (!formValues.role) {
      newErrors.role = 'Role is required';
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const surfaceServerError = (error, fallback) => {
    console.error('Member form error:', error);
    const msg =
      error?.response?.data?.message ||
      error?.response?.data?.Message ||
      error?.response?.data ||
      error?.message ||
      fallback;
    toast.error(typeof msg === 'string' ? msg : fallback);
  };

  // Performs the actual POST/PUT. Reused by the no-match path AND the confirm-dialog path.
  const submitMember = async () => {
    setLoading(true);
    try {
      // Invitations are sent from the member list / profile after add — not at
      // creation time. The server sets InvitationStatus from User.EmailConfirmed
      // (Accepted for existing portal users, NotInvited for newly-created ones).
      const memberData = {
        firstName: formValues.firstName.trim(),
        lastName: formValues.lastName.trim(),
        email: formValues.email.trim(),
        participationStatus: formValues.participationStatus,
        role: formValues.role,
        clubId,
      };

      if (isEdit) {
        const resp = await updateMember(clubId, member.email, memberData);
        toast.success('Member updated successfully');
        // Heads-up if promoting this member cascaded their guest athletes to full membership (and pulled any
        // co-contact up) to keep the family consistent — see role-and-deletion-design §5.6.
        notifyAthletePromotions(resp);
        notifyContactPromotions(resp);
      } else {
        await createClubMember(memberData);
        toast.success('Member added successfully');
      }
      setShowLookupDialog(false);
      setLookupResult(null);
      onSuccess();
    } catch (error) {
      surfaceServerError(
        error,
        isEdit ? 'Failed to update member profile' : 'Failed to add member profile'
      );
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async () => {
    if (!validateForm()) return;

    if (isEdit) {
      await submitMember();
      return;
    }

    // Add path: preflight lookup so we can either confirm (existing user) or
    // block (already in this club) BEFORE submitting.
    setLoading(true);
    try {
      const response = await lookupUserByEmail(
        clubId,
        formValues.email.trim()
      );
      if (response.data?.exists) {
        setLookupResult(response.data);
        setShowLookupDialog(true);
        setLoading(false);
        return;
      }
      // No matching account — fall through to POST (creates the user).
      await submitMember();
    } catch (error) {
      surfaceServerError(error, 'Failed to look up user by email');
      setLoading(false);
    }
  };

  const handleLookupConfirm = async () => {
    await submitMember();
  };

  const handleLookupCancel = () => {
    setShowLookupDialog(false);
    setLookupResult(null);
  };

  if (!show) return null;

  // Keyed off the original role so the extra option persists across in-form changes.
  const roleOptions = roleOptionsFor(member?.role);

  return (
    <>
      {/* No overlay click-to-dismiss — closing is deliberate (the × button), so a mis-click doesn't lose edits. */}
      <div className={styles.modalOverlay}>
        <div className={styles.modalContent}>
          <div className={styles.modalHeader}>
            <h2 className={styles.modalTitle}>
              {isEdit ? 'Edit Member' : 'Add New Member'}
            </h2>
            <button
              className={styles.closeBtn}
              onClick={onClose}
              aria-label="Close"
            >
              &times;
            </button>
          </div>

          <div className={styles.modalBody}>
            <div className={styles.form}>
              <NameInputBox
                label="Name"
                formValues={formValues}
                errors={errors}
                handleInputChange={handleInputChange}
              />

              {canEditEmail && (
                <CustomInput
                  label="Email Address"
                  name="email"
                  value={formValues.email}
                  placeholder="member@example.com"
                  onChange={handleInputChange}
                  error={errors.email}
                />
              )}

              {canEditParticipationStatus && (
                <CustomSelect
                  label="Membership Status"
                  options={participationStatusOptions}
                  value={formValues.participationStatus}
                  name="participationStatus"
                  error={errors.participationStatus}
                  onChange={handleInputChange}
                />
              )}

              {canEditRole && (
                <CustomDropdown
                  label="Role"
                  name="role"
                  value={formValues.role}
                  onChange={handleDropBoxChange}
                  options={roleOptions}
                  error={errors.role}
                  placeholder=""
                  isText={true}
                />
              )}

              {isEditingSelf && !canEditEmail && (
                <div className={styles.infoMessage}>
                  <p
                    style={{
                      color: '#6b7280',
                      fontSize: '0.875rem',
                      fontStyle: 'normal',
                    }}
                  >
                    Your registered email address can only be changed by a club
                    admin. Please contact your club admin to update it.
                  </p>
                </div>
              )}
            </div>
          </div>

          <div className={styles.modalFooter}>
            <button
              className={styles.btnSecondary}
              onClick={onClose}
              disabled={loading}
            >
              Cancel
            </button>
            <button
              className={styles.btnPrimary}
              onClick={handleSubmit}
              disabled={loading}
            >
              {loading ? 'Saving...' : isEdit ? 'Save Changes' : 'Add Member'}
            </button>
          </div>
        </div>
      </div>

      <UserLookupConfirmDialog
        show={showLookupDialog}
        lookupResult={lookupResult}
        purpose="member"
        context={{
          selectedRole: formValues.role,
          typedFirstName: formValues.firstName,
          typedLastName: formValues.lastName,
        }}
        loading={loading}
        onConfirm={handleLookupConfirm}
        onCancel={handleLookupCancel}
      />
    </>
  );
};

MemberFormModal.propTypes = {
  show: PropTypes.bool.isRequired,
  member: PropTypes.object,
  clubId: PropTypes.string.isRequired,
  onClose: PropTypes.func.isRequired,
  onSuccess: PropTypes.func.isRequired,
};

export default MemberFormModal;
